import { Box, styled } from '@mui/material';
import { LeftDrawerLayoutZIndex } from '@/constant/z-index';
import { GNBHeight } from '@/components/@IntranetLibrary/GNB/styles';

const ___ExpandedWidth = 362;
const ___NonExpandedWidth = 20;

const TopMargin = GNBHeight;

// 전체 레이아웃
export const Layout = styled(Box)`
  display: flex;
  overflow: hidden;
  position: absolute;
  width: 100vw;
  height: auto;
  left: 0;

  &.newHomeMode {
    position: static;
    height: 100%;
    width: 100%;
    left: initial;
  }
`;

// 사이드바 컨테이너
const SidebarContainerCustomPropKeys = [
  'isExpanded',
  'isSliding',
  'isHovering',
];
export const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !SidebarContainerCustomPropKeys.includes(prop as string),
})<{ isExpanded: boolean; isSliding: boolean; isHovering: boolean }>`
  width: ${(props) =>
    props.isExpanded ? `${___ExpandedWidth}px` : `${___NonExpandedWidth}px`};
  min-width: ${(props) =>
    props.isExpanded ? `${___ExpandedWidth}px` : `${___NonExpandedWidth}px`};
  transition: width 0.3s ease;
  z-index: ${LeftDrawerLayoutZIndex};

  @media (max-width: 1440px) {
    ${(props) =>
      props.isSliding && props.isHovering
        ? `
      width: ${___ExpandedWidth}px;
      min-width: ${___ExpandedWidth}px;
    `
        : ''}
  }
`;

// 사이드바
const StyledSidebarCustomPropKeys = [
  'isExpanded',
  'isHovering',
  'gnbHeight',
  'newHomeMode',
  'sidebarBackground',
];
export const StyledSidebar = styled(Box, {
  shouldForwardProp: (prop) =>
    !StyledSidebarCustomPropKeys.includes(prop as string),
})<{
  isExpanded: boolean;
  isHovering: boolean;
  gnbHeight?: number;
  newHomeMode?: boolean;
  sidebarBackground?: string;
}>`
  width: ${(props) =>
    props.isExpanded
      ? `${___ExpandedWidth}px`
      : props.isHovering
      ? `${___ExpandedWidth}px`
      : `${___NonExpandedWidth}px`};
  background-color: ${({ sidebarBackground }) =>
    sidebarBackground ?? '#f9fafb'};
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  color: #222;
  transition: width 0.3s ease;
  position: ${({ newHomeMode }) => (newHomeMode ? 'relative' : 'fixed')};
  display: flex;
  flex-direction: column;
  border-right: 1.5px solid #e7e9ec;
  top: ${({ newHomeMode, gnbHeight }) =>
    newHomeMode ? '' : gnbHeight ? `${gnbHeight}px` : `${TopMargin}`};
  bottom: 0;
  height: ${({ newHomeMode }) => (newHomeMode ? '100%' : '')};
  /* 열렸을 때도 항상 노출로 수정 */
  // button.toggle-button {
  //   opacity: ${(props) => (props.isExpanded ? 0 : 1)};
  // }
  //
  // &:hover button.toggle-button {
  //   opacity: 1;
  // }
`;

// 사이드바 내용
export const SidebarContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  position: relative;
  height: 100%;
  overflow: ${(props) => (props.visible ? 'auto' : 'hidden')};
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

// 사이드바 내부
export const SidebarInner = styled(Box)`
  min-width: ${___ExpandedWidth}px;
  width: 100%;
  height: 100%;
  position: relative;
`;

// 화살표 버튼
export const ToggleButton = styled('button')`
  position: absolute;
  top: 12.5px;
  right: -31px;
  background: transparent;
  cursor: pointer;
  width: 62px;
  height: 62px;
  padding: 15px;
  z-index: 20;
  transition: opacity 0.3s ease;
  pointer-events: auto;
`;

export const CircleBox = styled('div')`
  border: 1.5px solid #e7e9ec;
  border-radius: 32px;
  width: 32px;
  height: 32px;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

// 메인 컨텐츠
const MainContentCustomPropKeys = [
  'isExpanded',
  'isSliding',
  'isHovering',
  'contentMinWidth',
];
export const MainContent = styled(Box, {
  shouldForwardProp: (prop) =>
    !MainContentCustomPropKeys.includes(prop as string),
})<{
  isExpanded: boolean;
  isSliding: boolean;
  isHovering: boolean;
  contentMinWidth?: number;
}>`
  flex: 1 1 auto;
  min-width: ${(props) =>
    props.contentMinWidth ? `${props.contentMinWidth}px` : '0'};
  max-width: ${(props) =>
    props.contentMinWidth
      ? `calc(100vw - ${props.isExpanded ? '362px' : '20px'} - 64px)`
      : '100%'};
  background-color: white;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  transition: margin-left 0.3s ease, max-width 0.3s ease;
  overflow-x: hidden;

  @media (max-width: 1440px) {
    ${(props) =>
      props.isSliding && props.isHovering
        ? `
      margin-left: 0;
      ${
        /* contentMinWidth 가 없으면 폭을 좁히지 않는다 — 베이스 규칙(max-width:100%)과 동일.
           예전엔 삼항 양쪽이 같은 값이라 contentMinWidth 없이도 362+64 만큼 잘렸다 */
        props.contentMinWidth
          ? `max-width: calc(100vw - 362px - 64px);`
          : `max-width: 100%;`
      }
    `
        : props.contentMinWidth
        ? `
      max-width: calc(100vw - 20px - 64px);
    `
        : ''}
  }
`;

export const MainContentInner = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gnbHeight' && prop !== 'fillHeight',
})<{ gnbHeight?: number; fillHeight?: boolean }>`
  padding-left: calc((100% - var(--max-main-content)) / 2);
  padding-right: calc((100% - var(--max-main-content)) / 2);
  position: relative;
  /* fillHeight: 높이를 뷰포트에서 직접 재지 않고 부모가 준 높이를 그대로 쓴다.
       스크롤도 만들지 않는다 — 안쪽이 스스로 스크롤하는 화면(캘린더 격자 등)에서
       여기서 한 겹 더 만들면 스크롤바가 두 개 생긴다 */
  height: ${({ gnbHeight, fillHeight }) =>
    fillHeight
      ? '100%'
      : `calc(100vh - ${gnbHeight ? `${gnbHeight}px` : `${TopMargin}`})`};
  overflow-y: ${({ fillHeight }) => (fillHeight ? 'hidden' : 'auto')};
  overflow-x: hidden;
`;
